import { useMemo, useRef } from 'react'

import { useIsMyCalendlyScope } from 'components/meetings/filter_modal'

import { getWarningKey, type WarningKey } from '../utils'

/**
 * Batch hook for calculating warnings for all meetings in the list.
 * Processes meetings grouped by day to detect conflicts and other warning conditions.
 * Returns stable Map reference - only updates when warnings actually change.
 *
 * @param meetings - Array of meeting groups from RTK Query, where each group contains events for a day
 * @returns Map of meeting UUID to warning key (null if no warning)
 */
export const useBatchMeetingWarnings = (meetings: any[] = []): Map<string, WarningKey | null> => {
  const isMyCalendlyScope = useIsMyCalendlyScope()
  const prevMapRef = useRef<Map<string, WarningKey | null>>(new Map())

  const warningsMap = useMemo(() => {
    const newMap = new Map<string, WarningKey | null>()

    if (!meetings || !Array.isArray(meetings)) {
      if (prevMapRef.current.size === 0) return prevMapRef.current
      prevMapRef.current = newMap
      return newMap
    }

    let hasChanges = false

    meetings.forEach((group: any) => {
      const eventsOnDay = (group.events || []).filter((e: any) => !e.placeholder)

      eventsOnDay.forEach((event: any) => {
        if (!event?.uuid) return

        const otherEventsOnDay = eventsOnDay.filter((e: any) => e.uuid !== event.uuid)
        const warningKey = getWarningKey(event, isMyCalendlyScope, { otherEventsOnDay })

        // Always store the result (including null) to ensure proper change detection
        newMap.set(event.uuid, warningKey)

        if (prevMapRef.current.get(event.uuid) !== warningKey) {
          hasChanges = true
        }
      })
    })

    if (prevMapRef.current.size !== newMap.size) {
      hasChanges = true
    }

    if (!hasChanges) {
      return prevMapRef.current
    }

    prevMapRef.current = newMap
    return newMap
  }, [meetings, isMyCalendlyScope])

  return warningsMap
}
